import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import forEach from 'lodash/forEach.js';
import union from 'lodash/union.js';
import useOrgId from './useOrgId.js';
import {
  ACCESS_RIGHTS,
  DEFAULT_ROLE_PERMISSIONS,
  ONE_TO_MANY_PERMISSIONS,
  ONE_TO_ONE_PERMISSIONS,
  VOD_PERMISSIONS,
  CHAT_PERMISSIONS,
} from 'shared-permissions';
import {
  CHAT_PRODUCT,
  ONE_TO_MANY_PRODUCT,
  ONE_TO_ONE_PRODUCT,
  ON_DEMAND_PRODUCT,
} from 'constants/products.js';

const access = accessRights => typeof accessRights === 'string' ? [accessRights] : accessRights;

// Export to be able to simplify permission generation in unit tests
export const getUserPermissions = (roles, permissionOverrides = {}, rolePermissions = DEFAULT_ROLE_PERMISSIONS) => {
  const result = (roles || []).reduce((acc, role) => {
    forEach(((rolePermissions || {})[role] || {}), (accessRights, permission) =>
      acc[permission] = acc[permission] ? union(acc[permission], accessRights) : accessRights);
    return acc;
  }, {});
  forEach(permissionOverrides, (accessRights, permission) => result[permission] = accessRights);
  return result;
};

// Export to be able to unit test permission function
export const getPermissionFunction = permissions => (permission, accessRights = ACCESS_RIGHTS) =>
  access(accessRights).every(accessRight => permissions[permission]?.includes(accessRight));

const usePermissions = () => {
  const authUser = useSelector(state => state.sessionState.authUser);
  const masqueradeMode = useSelector((state) => state.ui?.masqueradeMode);
  const orgId = useOrgId();
  const { roles, permissionOverrides } = (typeof authUser?.membership === 'function' && authUser?.membership(orgId)?.user) || {};
  const { permissions, hasPermission } = useMemo(() => {
    const permissions = getUserPermissions(roles, permissionOverrides);
    if (masqueradeMode?.org?.id) {
      return {
        permissions,
        hasPermission: () => true,
      };
    }
    return {
      permissions,
      hasPermission: getPermissionFunction(permissions),
    };
  }, [roles, permissionOverrides, masqueradeMode?.org?.id]);
  const hasOrgPermission = useCallback((org) => {
    if (masqueradeMode?.org?.id) return () => true;
    const { roles, permissionOverrides } = (typeof authUser?.membership === 'function' && authUser?.membership(org)?.user) || {};
    const orgPermissions = getUserPermissions(roles, permissionOverrides);
    return getPermissionFunction(orgPermissions);
  }, [authUser, masqueradeMode?.org?.id]);

  const hasProductPermission = (product, permissions) => {
    switch (product) {
      case ONE_TO_ONE_PRODUCT:
        const oneToOnePermissions = Object.keys(ONE_TO_ONE_PERMISSIONS);
        return permissions.some(permission => oneToOnePermissions.includes(permission));
      case ONE_TO_MANY_PRODUCT:
        const oneToManyPermissions = Object.keys(ONE_TO_MANY_PERMISSIONS);
        return permissions.some(permission => oneToManyPermissions.includes(permission));
      case ON_DEMAND_PRODUCT:
        const vodPermissions = Object.keys(VOD_PERMISSIONS);
        return permissions.some(permission => vodPermissions.includes(permission));
      case CHAT_PRODUCT:
        const chatPermissions = Object.keys(CHAT_PERMISSIONS);
        return permissions.some((permission) => chatPermissions.includes(permission));
      default:
        return false;
    }
  };

  const hasOrgSpecificProductPermission = useCallback((org, product) => {
    if (masqueradeMode?.org?.id) return true;
    const { roles, permissionOverrides } = (typeof authUser?.membership === 'function' && authUser?.membership(org)?.user) || {};
    const orgPermissions = getUserPermissions(roles, permissionOverrides);
    return getPermissionFunction(orgPermissions)
      && hasProductPermission(product, Object.keys(orgPermissions));
  }, [authUser, masqueradeMode?.org?.id]);

  return {
    permissions,
    hasPermission,
    hasOrgPermission,
    hasOrgSpecificProductPermission,
  };
};

export const withPermissions = Component => (props) => {
  const { hasPermission, permissions } = usePermissions();
  return (
    <Component
      {...props}
      hasPermission={hasPermission}
      permissions={permissions}
    />
  );
};

export default usePermissions;
